import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import api from './api/axiosInstance';
import { useHeaderAction } from './HeaderActionContext';
import {
    MobileAiAssistantRegistrationContext,
    MobileAiAssistantStateContext,
    useMobileAiAssistantState,
} from './MobileAiAssistantContext';
import { ReactComponent as IcAngleRight } from './assets/img/ic-angle-right.svg';

export const AI_SOURCE_OPTIONS = [
    { value: 'abroadify', label: 'Abroadify' },
    // { value: 'wikipedia', label: 'Wikipedia' },
    // { value: 'government', label: 'Government' },
    // { value: 'universities', label: 'Universities' },
    // { value: 'companies', label: 'Company websites' },
];

// The shared mobile Ask Abroadify bottom sheet. A single instance is rendered
// by MobileAiAssistantProvider; it only ever shows on mobile viewports and
// only on pages that opted in via useMobileAiPageContext. Desktop inline
// Ask Abroadify cards are page-local and unaffected.
const MobileAiAssistantSheet = () => {
    const {
        isOpen,
        closeAssistant,
        aiQuery,
        setAiQuery,
        aiResult,
        aiLoading,
        aiError,
        selectedAiSources,
        toggleAiSource,
        submitAiQuery,
        pageContext,
        pageHandlersRef,
    } = useMobileAiAssistantState();

    const inputRef = useRef(null);

    const selectedSourceLabels = AI_SOURCE_OPTIONS
        .filter((source) => selectedAiSources.includes(source.value))
        .map((source) => source.label);
    const sourceLabel = selectedSourceLabels.length > 0
        ? `Using ${selectedSourceLabels.join(' and ')}`
        : '';

    const suggestions = Array.isArray(pageContext?.suggestions) ? pageContext.suggestions : [];
    const showSuggestions = (
        isOpen &&
        !aiLoading &&
        !aiResult &&
        !aiError &&
        suggestions.length > 0
    );

    const handleSuggestionClick = (suggestion) => {
        setAiQuery(suggestion);
        requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
    };

    const onApplyStructuredFilters = pageHandlersRef.current?.onApplyStructuredFilters;

    return (
        <Modal
            show={isOpen}
            onHide={closeAssistant}
            className="discovery-ai-mobile-modal"
            dialogClassName="discovery-ai-mobile-dialog"
            contentClassName="discovery-ai-mobile-content"
            aria-labelledby="discovery-ai-mobile-title"
        >
            <Modal.Header closeButton>
                <div className="discovery-ai-mobile-handle" aria-hidden="true" />
                <Modal.Title id="discovery-ai-mobile-title">Ask Abroadify</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="discovery-ai-card">
                    <div className="discovery-ai-input-row">
                        <div className="discovery-ai-input-wrap">
                            <input
                                ref={inputRef}
                                type="text"
                                className="form-control discovery-ai-input"
                                placeholder="Can you tell me..."
                                value={aiQuery}
                                disabled={aiLoading}
                                onChange={(e) => setAiQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        submitAiQuery();
                                    }
                                }}
                            />

                            <button
                                type="button"
                                className="discovery-ai-submit"
                                onClick={submitAiQuery}
                                disabled={aiLoading}
                                aria-label="Submit Ask Abroadify query"
                            >
                                <IcAngleRight className="discovery-ai-submit-icon" />
                            </button>
                        </div>
                    </div>

                    <div className="discovery-ai-source-row">
                        {AI_SOURCE_OPTIONS.map((source) => {
                            const isSelected = selectedAiSources.includes(source.value);
                            const isRequired = source.value === 'abroadify';

                            return (
                                <button
                                    key={source.value}
                                    type="button"
                                    className={`discovery-ai-source-chip ${isSelected ? 'is-selected' : ''}`}
                                    onClick={() => toggleAiSource(source.value)}
                                    disabled={isRequired}
                                >
                                    {isSelected && <span aria-hidden="true">&#10003; </span>}
                                    {source.label}
                                </button>
                            );
                        })}
                    </div>

                    {showSuggestions && (
                        <div className="discovery-ai-mobile-suggestions">
                            <div className="discovery-ai-mobile-suggestions-title">Suggested</div>
                            <div className="discovery-ai-mobile-suggestion-list">
                                {suggestions.map((suggestion) => (
                                    <button
                                        key={suggestion}
                                        type="button"
                                        className="discovery-ai-mobile-suggestion"
                                        onClick={() => handleSuggestionClick(suggestion)}
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {(aiLoading || aiResult || aiError) && (
                        <div className="discovery-ai-response">
                            <div className="discovery-ai-answer">
                                {aiLoading && <span className="discovery-ai-thinking">Thinking</span>}
                                {!aiLoading && aiError}
                                {!aiLoading && !aiError && aiResult?.answer}
                            </div>

                            <div className="discovery-ai-sources">
                                {sourceLabel}
                            </div>

                            {aiResult?.suggestedFilters?.length > 0 && typeof onApplyStructuredFilters === 'function' && (
                                <div className="discovery-ai-filters">
                                    <div className="discovery-ai-filter-chips">
                                        {aiResult.suggestedFilters.map((filter) => (
                                            <button
                                                key={filter}
                                                type="button"
                                                className="discovery-ai-filter-chip"
                                                onClick={() => onApplyStructuredFilters(aiResult.structuredFilters)}
                                            >
                                                {filter}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

// Owns the reusable mobile assistant state (open state, query, result,
// sources) and hosts the single shared bottom-sheet instance. While a page
// has registered context, it exposes the AI trigger through the existing
// generic mobileLeftAction header slot; other pages keep the default header.
const MobileAiAssistantProvider = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [aiQuery, setAiQuery] = useState('');
    const [aiResult, setAiResult] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState('');
    const [selectedAiSources, setSelectedAiSources] = useState(['abroadify']);
    const [pageContext, setPageContext] = useState(null);
    const pageHandlersRef = useRef({});
    const desktopInputRef = useRef(null);

    const location = useLocation();
    const { setMobileLeftAction } = useHeaderAction();

    // Same breakpoint the mobile discovery layout uses — media-breakpoint-down(md).
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });

    // Query/result/sources deliberately survive route changes; only the sheet
    // closes so it never overlays a page the user just navigated to.
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    const hasPageContext = Boolean(pageContext);

    // Desktop: only map-first discovery pages get the assistant (floating
    // launcher). Detail/info pages never do.
    const showDesktopLauncher = !isMobile && pageContext?.mapFirst === true;

    // Floating launcher: bring the inline desktop card into view and focus it.
    const focusDesktopInput = () => {
        const input = desktopInputRef.current;
        if (!input) return;
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input.focus({ preventScroll: true });
    };

    useEffect(() => {
        if (!isMobile || !hasPageContext) {
            setIsOpen(false);
            return undefined;
        }

        const mobileAiAction = {
            ariaLabel: 'Open Abroadify AI Assistant',
            onClick: () => setIsOpen(true),
            mapFirst: pageContext?.mapFirst === true,
            showBack: pageContext?.mapFirst === true && pageContext?.mapScope === 'scoped',
        };

        setMobileLeftAction(mobileAiAction);

        return () => {
            setMobileLeftAction((currentAction) => (
                currentAction === mobileAiAction ? null : currentAction
            ));
        };
    }, [isMobile, hasPageContext, pageContext?.mapFirst, pageContext?.mapScope, setMobileLeftAction]);

    const toggleAiSource = (sourceValue) => {
        setSelectedAiSources((prev) => {
            if (sourceValue === 'abroadify') return prev;

            return prev.includes(sourceValue)
                ? prev.filter((item) => item !== sourceValue)
                : [...prev, sourceValue];
        });
    };

    const submitAiQuery = async () => {
        const trimmedQuery = aiQuery.trim();

        if (!trimmedQuery || aiLoading) return;

        setAiLoading(true);
        setAiError('');
        setAiResult(null);

        try {
            const res = await api.post('/api/discovery-ai/query', {
                prompt: trimmedQuery,
                sources: selectedAiSources,
                discoveryType: pageContext?.discoveryType,
            });

            setAiResult({
                answer: res.data?.answer || '',
                suggestedFilters: Array.isArray(res.data?.suggestedFilters) ? res.data.suggestedFilters : [],
                structuredFilters: res.data?.structuredFilters || null,
                targetType: res.data?.targetType || pageContext?.discoveryType || '',
            });
        } catch (error) {
            console.error('Discovery AI query failed:', error);
            setAiError('Abroadify AI could not answer right now. Please try again.');
        } finally {
            setAiLoading(false);
        }
    };

    const registerPageContext = useCallback((context) => {
        setPageContext(context);
    }, []);

    // Stable value: pages consuming only the registration context never
    // re-render when assistant state (keystrokes, loading, results) changes.
    const registrationValue = useMemo(() => ({
        registerPageContext,
        pageHandlersRef,
        desktopInputRef,
    }), [registerPageContext]);

    const stateValue = {
        isOpen,
        closeAssistant: () => setIsOpen(false),
        aiQuery,
        setAiQuery,
        aiResult,
        aiLoading,
        aiError,
        selectedAiSources,
        toggleAiSource,
        submitAiQuery,
        pageContext,
        pageHandlersRef,
    };

    return (
        <MobileAiAssistantRegistrationContext.Provider value={registrationValue}>
            <MobileAiAssistantStateContext.Provider value={stateValue}>
                {children}
                {showDesktopLauncher && (
                    <button
                        type="button"
                        className="discovery-ai-desktop-launcher"
                        onClick={focusDesktopInput}
                        aria-label="Go to Ask Abroadify"
                    >
                        Ask Abroadify
                    </button>
                )}
                <MobileAiAssistantSheet />
            </MobileAiAssistantStateContext.Provider>
        </MobileAiAssistantRegistrationContext.Provider>
    );
};

export default MobileAiAssistantProvider;
